// src/components/StatusBadge.tsx
//
// Status carries a text label, never colour alone (ux-direction §3.8, and the
// build brief's "every table must be readable without colour"). `SgdsBadge`
// renders the text we give it, so the colour is decoration on top of a real
// word — not a substitute for one.
import { SgdsBadge } from '@govtechsg/sgds-web-component/react'
import type { BillStatus } from '../lib/types'
import { billStatusLabel, billTypeLabel, settlementStateLabel } from '../lib/format'
import { actionWords, plainCode, roleWords } from '../lib/plain-words'
import {
  AUDIT_OUTCOME_LABELS,
  isAuditOutcome,
  outcomeGroundLabel,
  outcomeGroundOf,
  type AuditOutcome,
} from '../lib/audit-outcome'

type BadgeVariant = 'primary' | 'accent' | 'success' | 'danger' | 'warning' | 'purple' | 'neutral'

const BILL_STATUS_VARIANT: Record<BillStatus, BadgeVariant> = {
  SUBMITTED: 'neutral',
  AWAITING_L1: 'warning',
  AWAITING_L2: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  // ★★ WAVE 10 — `purple`, and the choice is CONSTRAINED rather than free:
  //
  //  * NOT `warning`, which both AWAITING_* states already carry. A paused bill is
  //    the one state where no approval action will do anything, so rendering it in
  //    the same colour as "waiting for you to approve it" would collapse exactly
  //    the distinction this status exists to make.
  //  * NOT `accent`. `ux-direction.md` §3.2a measures SGDS accent
  //    (`--sgds-blue-700 #0151A0`) at 1.41:1 / ΔE 11.5 against PSD navy #00405F —
  //    two blues a reader cannot separate, ruled out once already on this project.
  //  * NOT `neutral`, which SUBMITTED holds, and which reads as "unremarkable".
  //
  // `purple` is a published SGDS hue ramp, already measured for the audit
  // `REFUSED` badge, and it collides with nothing else on a bill screen — audit
  // outcome badges do not appear there. And as everywhere in this app, the LABEL
  // carries the meaning: no information here is conveyed by colour alone.
  INFORMATION_REQUESTED: 'purple',
}

export function BillStatusBadge({ status }: { status: string }) {
  const variant = BILL_STATUS_VARIANT[status as BillStatus] ?? 'neutral'
  return <SgdsBadge variant={variant}>{billStatusLabel(status)}</SgdsBadge>
}

/**
 * ⚠ FOUR outcomes since migration 009, not three.
 *
 * `REFUSED` is `purple`, and the choice is constrained rather than free:
 *
 *  - not `success`/`danger`/`warning` — those are ALLOWED, DENIED and ERROR, and
 *    the whole point of the split is that a 409 must not be mistaken for either
 *    a success or a security event;
 *  - not `accent`, `cyan` or `primary` — SGDS's accent resolves to
 *    `--sgds-blue-700 #0151A0`, which `ux-direction.md` §3.2a measures at
 *    **1.41:1 / ΔE 11.5** against PSD navy `#00405F`. Two blues a reader cannot
 *    separate is the documented failure this project already ruled out once;
 *  - `neutral` was the other candidate and was rejected on meaning, not
 *    contrast: grey reads as "unremarkable", and a refusal is not nothing.
 *
 * `purple` is a published SGDS hue ramp (`--sgds-purple-100..900`, shipped in
 * the installed package's `themes/root.css`), it collides with nothing else on
 * this screen, and it is measured in the browser rather than assumed — see the
 * contrast figures in the task report.
 */
const AUDIT_OUTCOME_VARIANT: Readonly<Record<AuditOutcome, BadgeVariant>> = {
  ALLOWED: 'success',
  REFUSED: 'purple',
  DENIED: 'danger',
  ERROR: 'warning',
}

/**
 * ⚠ Renders the RAW value for anything it does not recognise, never nothing.
 *
 * `outcome` is cast from JSON, so a `Record<AuditOutcome, …>` lookup on it is a
 * runtime hole: when `REFUSED` started arriving, both maps returned `undefined`
 * and this badge rendered an empty label with no variant, silently, on the
 * compliance surface. Typecheck could not have caught it and did not.
 *
 * So the fifth value — whenever the gateway adds one — shows up as a grey badge
 * carrying its own name. That is legible, obviously unstyled, and impossible to
 * mistake for a known state, which is everything an unknown value needs to be.
 */
export function AuditOutcomeBadge({ outcome }: { outcome: string }) {
  if (!isAuditOutcome(outcome)) {
    return (
      <SgdsBadge variant="neutral" title={`"${outcome}" is not an outcome this build recognises`}>
        {outcome}
      </SgdsBadge>
    )
  }
  return <SgdsBadge variant={AUDIT_OUTCOME_VARIANT[outcome]}>{AUDIT_OUTCOME_LABELS[outcome]}</SgdsBadge>
}

/**
 * The outcome AND the grounds for it — the pair, in one cell.
 *
 * ⚠ `detail.outcomeReason` is the entire reason migration 009 split the enum,
 * and without it on screen the split is invisible: `DENIED` alone cannot tell a
 * compliance reader whether a caller was refused a record they may not see
 * (`scope`) or asked for one that does not exist (`absent`) — and on the wire
 * both were the same 404 with the same body, deliberately, so the ledger is the
 * only place that difference survives at all.
 *
 * Rendered as a second line under the badge rather than as a seventh table
 * column: the reason qualifies the outcome, so separating them across the table
 * would make the reader join two columns by eye on every row, and the audit
 * table is already at six.
 *
 * A row with no recognisable reason says so. That covers events written before
 * 009 — the migration's own marker row records where the classification changed
 * — and it is a different statement from any of the ten real reasons.
 *
 * Withheld is a THIRD statement again, and which of the three a row gets is
 * decided by `outcomeGroundOf` rather than by a ternary here. That is not
 * tidiness: a redacted row also has no recognisable reason, so asking for the
 * reason first reports a working control (S-014) as a gap in the compliance
 * record. The ordering lives in one tested function that this cell and the page
 * legend both read.
 *
 * ⚠ This comment used to end "and this app has no DOM to render a component
 * into, so a ternary here could be reordered and nothing would fail." That is no
 * longer true and the sentence is removed rather than left as a reason nobody
 * needs to act on: `StatusBadge.dom.test.tsx` mounts these components in jsdom
 * with the SGDS custom elements registered, and asserts the `variant` each
 * outcome reaches the element with — the prop `renderToStaticMarkup` could not
 * see. The one-tested-function discipline stands on its own merits.
 */
export function AuditOutcomeCell({ outcome, detail }: { outcome: string; detail?: Record<string, unknown> }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <AuditOutcomeBadge outcome={outcome} />
      <span className="text-xs text-[var(--sgds-body-color-subtle)] leading-snug">
        {outcomeGroundLabel(outcomeGroundOf(detail))}
      </span>
    </div>
  )
}

/** High / Medium / Low — EXACTLY the three severity levels P2C10 Table 10-1 defines. Never a fourth. */
export function SeverityBadge({ level }: { level: 'High' | 'Medium' | 'Low' }) {
  const variant: BadgeVariant = level === 'High' ? 'danger' : level === 'Medium' ? 'warning' : 'accent'
  return <SgdsBadge variant={variant}>{level}</SgdsBadge>
}

// ════════════════════════════════════════════════════════════════════════════════
// ★★ ONE CATEGORY MAPPING FOR THE WHOLE SYSTEM (owner direction, 2026-10-15)
//
// A category word ("Input checked", "Refused", "Draft", "Passed") is drawn by `SgdsBadge`, in the colour this
// table gives its meaning, on EVERY screen. Nothing else in the app invents a category chip.
//
// ⚠ The WORD is always the label and is always visible: colour is decoration on a real word (WCAG 1.4.1,
// ux-direction §3.8). ⚠ Only hues already measured on this project are used (see the contrast pairs in
// `styles/token-cascade.test.ts`); `accent`, `cyan` and `info` stay out because SGDS blue sits beside PSD navy
// at 1.41:1 / ΔE 11.5 (§3.2a).
//
//   hue        meaning across the app
//   ---------  ---------------------------------------------------------------------------------------------
//   neutral    a check or a record that carries no verdict of its own; superseded; unchanged
//   primary    a selection or a match (navy: the one calm hue that is not a status colour)
//   success    a result, a pass, in force, added
//   warning    still open, waiting, or changed
//   danger     failed, rejected, removed, stopped
//   purple     REFUSED, everywhere (same as the audit outcome REFUSED)
// ════════════════════════════════════════════════════════════════════════════════

export type CategoryKind =
  | 'trace'
  | 'outcome'
  | 'ruleStatus'
  | 'verdict'
  | 'diff'
  | 'refusal'
  | 'state'
  | 'settlement'
  | 'priority'
  | 'billType'
  | 'role'
  | 'contactKind'
  | 'agencyKind'
  | 'severity'
  | 'grant'
  | 'wardCategory'
  | 'schemeCategory'
  | 'code'
  | 'action'
  | 'source'
  | 'disposition'

const CATEGORY_VARIANT: Readonly<Record<CategoryKind, Readonly<Record<string, BadgeVariant>>>> = {
  // (a) calculation trace step codes
  trace: {
    INPUT_VALIDATED: 'neutral',
    INPUT_CONSISTENT: 'neutral',
    ENTITLEMENT_COVERS: 'neutral',
    CAP_STATUS: 'neutral',
    REQUIRED_INPUTS: 'neutral',
    CANDIDATES: 'primary',
    RULE_SELECTED: 'primary',
    ROW_MATCHED: 'primary',
    COMPONENT_CALCULATED: 'success',
    TOTALLED: 'success',
    REFUSED: 'purple',
  },
  // (b) a calculation's outcome
  outcome: { CALCULATED: 'success', REFUSED: 'purple' },
  // (c) a rule version's status
  ruleStatus: { ACTIVE: 'success', DRAFT: 'warning', SUPERSEDED: 'neutral', RETIRED: 'danger' },
  // (d) a test case's verdict
  verdict: { PASSED: 'success', FAILED: 'danger' },
  // (e) the state of a line in a definition comparison
  diff: { ADDED: 'success', CHANGED: 'warning', REMOVED: 'danger', UNCHANGED: 'neutral' },
  // (f) every refusal kind is a refusal: one hue, the word names the kind
  refusal: {},
  // (g) the plain states other screens carry: delivery, ticket, template, catalogue, file, sub-system
  state: {
    // done or healthy
    SENT: 'success', DELIVERED: 'success', COMPLETED: 'success', COMPLETE: 'success', SETTLED: 'success',
    RESOLVED: 'success', ACTIVE: 'success', ENABLED: 'success', PUBLISHED: 'success', VERIFIED: 'success',
    PROCESSED: 'success', COLLECTED: 'success', PAID: 'success', OK: 'success', DISABLED_BY_ADMIN: 'neutral',
    // storage and retention levels
    WARNING: 'warning', CRITICAL: 'danger',
    // open, waiting or in motion (primary is a selection or a match: work that is under way)
    PENDING: 'warning', QUEUED: 'warning', DRAFT: 'warning', OPEN: 'warning', SCHEDULED: 'warning',
    NOT_SENT: 'warning', UNVERIFIED: 'warning', PARTIALLY_PAID: 'warning', RECEIVED: 'warning',
    SENDING: 'primary', PROCESSING: 'primary', IN_PROGRESS: 'primary', RUNNING: 'primary', CLAIMED: 'primary',
    GENERATED: 'primary',
    // problems
    FAILED: 'danger', ERROR: 'danger', BOUNCED: 'danger', ABANDONED: 'danger', REJECTED: 'danger', OVERDUE: 'danger',
    EXPIRED: 'danger', DECOMMISSIONED: 'danger', PAYMENT_REJECTED: 'danger',
    // set aside, or not a real delivery (SIMULATED must never read as SENT)
    CLOSED: 'neutral', DISABLED: 'neutral', INACTIVE: 'neutral', SUPERSEDED: 'neutral', RETIRED: 'neutral',
    WITHDRAWN: 'neutral', CANCELLED: 'neutral', NONE: 'neutral', SIMULATED: 'neutral', ARCHIVED: 'neutral',
    // a record delivery (accepted or rejected), a re-proration (re-prorated or referred to an officer), and a register check
    ACCEPTED: 'success', REPRORATED: 'success', REFERRED: 'warning', AGREES: 'success', DISAGREES: 'danger', NO_ENTRY: 'danger',
  },
  // A bill's settlement state: the Payment Agency's axis (not the bill's approval status)
  settlement: {
    NOT_SETTLED: 'warning', EMITTED: 'primary', PAID: 'success', PARTIALLY_PAID: 'warning',
    PAYMENT_REJECTED: 'danger', OVERPAID: 'danger',
  },
  // Priority: how soon, not how bad it went
  priority: { LOW: 'neutral', MEDIUM: 'primary', HIGH: 'warning', CRITICAL: 'danger' },
  // (h) the kind of a bill: a new bill is the ordinary case (neutral); an adjustment is a change to a bill already
  // priced, so it takes the one calm navy. Direction is carried by the WORD (upward or downward), never by hue:
  // red or green here would read as good or bad news, and an adjustment is neither.
  billType: { NEW_BILL: 'neutral', UPWARD_ADJUSTMENT: 'primary', DOWNWARD_ADJUSTMENT: 'primary' },
  // (i) a role: the Public Service Division is the administering role (navy); every other role is a neutral label.
  role: { PSD_USER: 'primary' },
  // (j) a contact's agency kind and (k) a mapping's agency kind: which side of the money. Navy marks the payer's side.
  contactKind: { HSP: 'primary', PAYMENT: 'primary', RECOVERY: 'neutral', ENTITLEMENT: 'neutral' },
  agencyKind: { PAYMENT: 'primary', RECOVERY: 'neutral' },
  // (l) severity of a finding: how soon it needs a person. Never accent, cyan or info.
  severity: { HIGH: 'danger', ATTENTION: 'warning', MEDIUM: 'warning', REVIEW: 'neutral', LOW: 'neutral', INFORMATION: 'neutral' },
  // (m) a Yes or No cell in a permission matrix: Yes is navy (a grant is a fact, not good news), No is neutral
  grant: { YES: 'primary', NO: 'neutral' },
  // (n) a ward class category and (o) a scheme category: the catalogue's own groupings, navy for the subsidised and
  // inpatient groups, neutral for the rest
  wardCategory: { SUBSIDISED: 'primary', HIGHLY_SUBSIDISED: 'primary' },
  schemeCategory: { INPATIENT: 'primary', OUTPATIENT: 'neutral', UNCLASSIFIED: 'neutral' },
  // (p) a catalogue code (an email template, a sign-in provider): an identifier, so neutral. The demonstration
  // roster is the one that is not a real sign-in, so it is warning, never success.
  code: { DEMO_ROSTER: 'warning' },
  // (q) what was done, resolved by `actionVariant` below (an audit action code or a request line): the table is empty
  action: {},
  // (r) where an event came from (gateway, console): neutral, the word says which
  source: {},
  // (s) what the retention policy does with a class of records when its period ends: keeping is the calm default,
  // archiving is work under way (navy), a person's decision is open (warning), disposal is the irreversible one (danger)
  disposition: { RETAIN_INDEFINITELY: 'neutral', ARCHIVE_THEN_RETAIN: 'primary', REVIEW_AT_EXPIRY: 'warning', DISPOSE_AT_EXPIRY: 'danger' },
}

/** The hue of an audit action: what it did to the record, read from the verb and never from the whole code. */
function actionVariant(action: string): BadgeVariant {
  const a = action.toUpperCase()
  if (/REFUSED/.test(a) || /^POST .*\/(?:REFUSE)/.test(a)) return 'purple'
  if (/FAILED|DENIED|REMOVED|RETIRED|DELETE/.test(a)) return 'danger'
  if (/ACTIVATED|CALCULATED|APPROVED|SIGNED_IN/.test(a)) return 'success'
  if (/DRAFTED|EDITED|CHANGED|^PUT |^PATCH /.test(a)) return 'warning'
  if (/^POST /.test(a)) return 'primary'
  return 'neutral'
}

/** The hue a category value draws in. A value it does not know is neutral and still shows its own word. */
export function categoryVariant(kind: CategoryKind, value: string): BadgeVariant {
  if (kind === 'refusal') return 'purple'
  if (kind === 'action') return actionVariant(value)
  return CATEGORY_VARIANT[kind][value.toUpperCase().replace(/[\s-]+/g, '_')] ?? 'neutral'
}

/**
 * A category as a coloured badge. `value` is the machine value (or the word itself for `state`); `label` is
 * the words shown (defaults to `value`). The label is what a screen reader and every test reads.
 */
export function CategoryBadge({ kind, value, label }: { kind: CategoryKind; value: string; label?: string }) {
  return (
    <SgdsBadge variant={categoryVariant(kind, value)} data-category={kind} data-value={value}>
      {label ?? value}
    </SgdsBadge>
  )
}

/** "IN_PROGRESS" -> "In progress". The word an officer reads for a stored upper-case state. */
export function stateLabel(value: string): string {
  const words = value.replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A plain state, priority or settlement state as the shared coloured badge, with its words humanised. */
export function StateBadge({ value, kind = 'state' }: { value: string; kind?: 'state' | 'settlement' | 'priority' }) {
  return <CategoryBadge kind={kind} value={value} label={kind === 'settlement' ? settlementStateLabel(value) : stateLabel(value)} />
}

/** A role as its plain name in the role colour; the stored code is only the tooltip. */
export function RoleBadge({ role }: { role: string }) {
  return (
    <SgdsBadge variant={categoryVariant('role', role)} data-category="role" data-value={role} title={role}>
      {roleWords(role)}
    </SgdsBadge>
  )
}

/** What was done, as words an officer reads: an audit action code or a recorded request line. The raw text is the tooltip. */
export function ActionBadge({ action }: { action: string }) {
  return (
    <SgdsBadge variant={categoryVariant('action', action)} data-category="action" data-value={action} title={action}>
      {actionWords(action)}
    </SgdsBadge>
  )
}

/** A stored code (template, sign-in provider, retention subject) as its plain name; the code is the tooltip. */
export function CodeBadge({ kind = 'code', value }: { kind?: CategoryKind; value: string }) {
  return (
    <SgdsBadge variant={categoryVariant(kind, value)} data-category={kind} data-value={value} title={value}>
      {plainCode(value)}
    </SgdsBadge>
  )
}

/** New bill, Upward adjustment, Downward adjustment: the bill type word in the shared bill-type hue. */
export function BillTypeBadge({ billType }: { billType: string }) {
  return <CategoryBadge kind="billType" value={billType} label={billTypeLabel(billType)} />
}
